import i18next from 'i18next';
import { call } from 'redux-saga/effects';
import { requestPuppy, isApiError } from '@wix/da-http-client';
import {
  PapiDaSharedGallectionFolders,
  PapiDaSharedGallectionFoldersCreate,
  PapiDaSharedCollectionsCollect,
  PapiDaSharedCollectionsCollectionsForDeviation,
  PapiGallection,
  PapiRequestDaSharedGallectionFolders,
  PapiRequestDaSharedGallectionFoldersCreate,
  PapiRequestDaSharedCollectionsCollect,
  PapiRequestDaSharedCollectionsCollectionsForDeviation,
  PapiRequestDaSharedCollectionsFave,
  PapiDaSharedCollectionsFave,
  PapiRequestDaSharedCollectionsUnfave,
  PapiDaSharedCollectionsUnfave,
} from '@wix/da-papi-types';
import { putErrorNotification } from '../../utils/saga';

let fakeCollections: PapiGallection[] = [];

function getFakeCollections() {
  if (process.env.NODE_ENV !== 'production') {
    const collections: PapiGallection[] =
      // eslint-disable-next-line global-require
      require('@wix/da-sample-data').collections;

    // move Featured folder to first
    let featured: PapiGallection | null = null;
    const nonFeatured: PapiGallection[] = [];
    for (const collection of collections) {
      if (collection.name === 'Featured') {
        featured = collection;
      } else {
        nonFeatured.push(collection);
      }
    }

    // duplicate non-featured so we can get some pagination
    // but randomize ids so the collections aren't deduped in the Stream
    const additionalCollections = [
      ...nonFeatured,
      ...nonFeatured,
      ...nonFeatured,
    ].map(c => ({
      ...c,
      folderId: Math.floor(Math.random() * 1000),
    }));

    fakeCollections = [
      ...(featured ? [featured] : []),
      ...nonFeatured,
      ...additionalCollections,
    ];
  }
  return fakeCollections;
}

export function* doFetchCollections(
  isPrivateCollections,
  offset = 0,
  limit = 20
) {
  const params: PapiRequestDaSharedGallectionFolders = {
    type: isPrivateCollections ? 'private_collection' : 'collection',
    offset,
    limit,
  };

  const result: PapiDaSharedGallectionFolders | false = yield call(
    requestPuppy,
    {
      method: 'get',
      url: 'gallection/folders',
      params,
    },
    () => {
      const collections = getFakeCollections();
      const nextOffset = offset + limit;
      const hasMore = collections.length - 1 > nextOffset;
      return {
        hasMore,
        nextOffset: hasMore ? nextOffset : null,
        results: collections.slice(offset, nextOffset),
      };
    }
  );

  return result;
}

export function* doFetchContainingCollectionIds(
  deviationId: number,
  isPrivateCollections: boolean
) {
  const params: PapiRequestDaSharedCollectionsCollectionsForDeviation = {
    deviationid: deviationId,
  };

  const url = isPrivateCollections
    ? 'privatecollections/collections_for_deviation'
    : 'collections/collections_for_deviation';

  const result: PapiDaSharedCollectionsCollectionsForDeviation | false =
    yield call(
      requestPuppy,
      {
        method: 'get',
        url,
        params,
      },
      () => {
        const res: PapiDaSharedCollectionsCollectionsForDeviation = {
          collectionIds: [],
        };
        return res;
      }
    );

  return result && result.collectionIds;
}

export function* doCommitCollections(
  folderIds: number[],
  deviationId: number,
  isPrivateCollections: boolean
) {
  const data: PapiRequestDaSharedCollectionsCollect = {
    itemid: deviationId,
    folderids: folderIds.length > 0 ? folderIds : undefined,
  };

  const url = isPrivateCollections
    ? 'privatecollections/collect'
    : 'collections/collect';

  const result: PapiDaSharedCollectionsCollect | false = yield call(
    requestPuppy,
    {
      method: 'post',
      url,
      data,
    },
    () => ({
      success: true,
    })
  );

  return result;
}

export function* doCreateCollection(
  name: string,
  isPrivateCollections: boolean
) {
  const data: PapiRequestDaSharedGallectionFoldersCreate = {
    name,
    type: isPrivateCollections ? 'private_collection' : 'collection',
  };
  const result: PapiDaSharedGallectionFoldersCreate | false = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'gallection/folders/create',
      data,
    },
    ({ data: { name: fakeName } }) => ({
      success: true,
      gallection: {
        ...getFakeCollections()[0],
        folderId: Math.round(Math.random() * 100000),
        name: fakeName,
      },
    })
  );
  if (isApiError(result)) {
    const { errorDescription } = result;
    yield putErrorNotification(
      i18next.t('collectionPanel.error.newCollection', { errorDescription })
    );
    return;
  }
  return result || { success: false };
}

export function* doFave(deviationId: number) {
  const data: PapiRequestDaSharedCollectionsFave = {
    itemid: deviationId,
  };

  const result: PapiDaSharedCollectionsFave | false = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'collections/fave',
      data,
    },
    () => ({ success: true })
  );

  return result || { success: false };
}

export function* doUnfave(deviationId: number) {
  const data: PapiRequestDaSharedCollectionsUnfave = {
    itemid: deviationId,
  };

  const result: PapiDaSharedCollectionsUnfave | false = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'collections/unfave',
      data,
    },
    () => ({ success: true })
  );

  return result || { success: false };
}
